// React is auto-imported with JSX transform
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, MessageSquare } from 'lucide-react';
import { useCommunity } from '../../hooks/useCommunity';
import { useThreads } from '../../hooks/useThreads';
import { PinnedMessages } from './PinnedMessages';
import { MessageList } from './MessageList';
import { NewMessageForm } from './NewMessageForm';
import { CategoryFilter } from './CategoryFilter';
import { ThreadTree } from './ThreadTree';
import { MessageThreadModal } from './MessageThreadModal';
import { ThreadReplyForm } from './ThreadReplyForm';
import { CategoryLoadingIndicator } from './CategoryLoadingIndicator';
import { OnlineMembers } from './OnlineMembers';
import { supabase } from '../../lib/supabase';
import type { ChatCategory } from '../../types/community';

export default function Community() {
  // Desktop OnlineMembers component
  // Component state
  const [viewMode, setViewMode] = useState<'flat' | 'threaded'>('flat');
  const [selectedThread, setSelectedThread] = useState<string | null>(null);
  const [isThreadModalOpen, setIsThreadModalOpen] = useState(false);
  const [showNewThreadForm, setShowNewThreadForm] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Category modal state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedMessageForCategory, setSelectedMessageForCategory] = useState<{
    id: string;
    currentCategory: ChatCategory | null;
  } | null>(null);
  
  // Parse URL params for thread view
  useEffect(() => {
    const url = new URL(window.location.href);
    const threadId = url.searchParams.get('thread');
    if (threadId) {
      setSelectedThread(threadId);
      setIsThreadModalOpen(true);
    }
  }, []);
  
  // Previously had Safari detection here, now using dynamic viewport height
  
  // Online members indicator is handled by OnlineMembers component now (mobile + desktop)
  
  // State for category switching loading
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const previousCategoryRef = useRef<string | null>(null);
  
  // Standard community data
  const { 
    messages, 
    pinnedMessages, 
    polls, 
    loading: communityLoading, 
    error: communityError, 
    currentUser, 
    isAdmin,
    categories,
    selectedCategory,
    setSelectedCategory: originalSetSelectedCategory,
    togglePin,
    deleteMessage: deleteCommunityMessage, 
    addMessage,
    addComment,
    createPoll,
    votePoll,
    deletePoll,
    toggleLike: toggleCommunityLike,
    hasMore,
    isLoadingMore,
    loadMoreMessages,
  } = useCommunity();
  
  // Wrap the category selection to add loading state
  const setSelectedCategory = (categoryId: string | null) => {
    if (categoryId !== selectedCategory) {
      setIsCategoryLoading(true);
      previousCategoryRef.current = selectedCategory;
      originalSetSelectedCategory(categoryId);
      
      // Simulate loading for at least 800ms to prevent flicker
      setTimeout(() => {
        setIsCategoryLoading(false);
      }, 800);
    }
  };
  
  // Thread-specific data and operations
  const {
    threadedMessages,
    loading: threadsLoading,
    error: threadsError,
    createReply,
    editMessage,
    toggleLike: toggleThreadLike,
    deleteMessage: deleteThreadMessage,
  } = useThreads(viewMode === 'threaded' ? selectedThread : undefined);

  // Combine errors and loading states
  const initialLoading = communityLoading || threadsLoading;
  const loading = initialLoading || isCategoryLoading;
  const error = communityError || threadsError;
  
  // Handle navigation back from thread view
  const handleBackToFlatView = () => {
    setViewMode('flat');
    setSelectedThread(null);
    // Update URL without reloading
    const newUrl = window.location.pathname;
    window.history.pushState({}, '', newUrl);
    // Scroll back to top
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  };
  
  // Handle selecting a thread
  const handleSelectThread = (threadId: string) => {
    setSelectedThread(threadId);
    setIsThreadModalOpen(true);
    // Update URL without reloading
    const newUrl = `${window.location.pathname}?thread=${threadId}`;
    window.history.pushState({}, '', newUrl);
    // Scroll back to top
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  };

  const handleCloseThreadModal = () => {
    setIsThreadModalOpen(false);
    setSelectedThread(null);
    const newUrl = window.location.pathname;
    window.history.pushState({}, '', newUrl);
  };
  
  // Handle creating a new thread
  const handleCreateThread = async (content: string) => {
    await addMessage(content, selectedCategory || '');
    setShowNewThreadForm(false);
  };
  
  // Handle adding a reply in thread view
  const handleAddReply = async (content: string, parentId: string) => {
    await createReply(content, parentId);
  };
  
  // Handle message actions based on view mode
  const handleToggleLike = (messageId: string) => {
    if (viewMode === 'threaded') {
      return toggleThreadLike(messageId);
    } else {
      return toggleCommunityLike(messageId);
    }
  };

  // Infinite scroll: load more when near bottom
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onScroll = () => {
      if (!hasMore || isLoadingMore || loading) return;
      const threshold = 400; // px from bottom
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom < threshold) {
        loadMoreMessages();
      }
    };

    el.addEventListener('scroll', onScroll);
    return () => {
      el.removeEventListener('scroll', onScroll);
    };
  }, [hasMore, isLoadingMore, loading, loadMoreMessages]);

  // Handle opening category modal
  const handleOpenCategoryModal = (messageId: string) => {
    // Find current category for this message
    const message = messages.find(m => m.id === messageId);
    const currentCategory = message?.category_id 
      ? categories.find(cat => cat.id === message.category_id) || null
      : null;
    
    setSelectedMessageForCategory({ id: messageId, currentCategory });
    setShowCategoryModal(true);
  };

  // Handle category change for messages
  const handleChangeCategory = async (categoryId: string) => {
    if (!selectedMessageForCategory) return;
    
    try {
      const { error } = await supabase
        .from('messages')
        .update({ category_id: categoryId })
        .eq('id', selectedMessageForCategory.id);
      
      if (error) throw error;
      
      // Close modal
      setShowCategoryModal(false);
      setSelectedMessageForCategory(null);
      
      // Show success message
      const toast = document.createElement('div');
      toast.className = 'fixed top-4 right-4 z-50 bg-green-500/20 border border-green-500/30 text-green-300 px-4 py-2 rounded-lg backdrop-blur-md shadow-lg text-sm font-medium';
      toast.textContent = 'Message moved to category!';
      document.body.appendChild(toast);
      setTimeout(() => {
        toast.remove();
      }, 2000);
    } catch (err) {
      console.error('Error changing category:', err);
      // Show error message
      const toast = document.createElement('div');
      toast.className = 'fixed top-4 right-4 z-50 bg-red-500/20 border border-red-500/30 text-red-300 px-4 py-2 rounded-lg backdrop-blur-md shadow-lg text-sm font-medium';
      toast.textContent = 'Failed to move message!';
      document.body.appendChild(toast);
      setTimeout(() => {
        toast.remove();
      }, 2000);
    }
  };
  
  // Handle message deletion for both threaded and flat views
  const handleDeleteMessage = (messageId: string, userId: string = '') => {
    if (viewMode === 'threaded') {
      // Thread message deletion only requires messageId
      return deleteThreadMessage(messageId);
    } else {
      // For community messages, wrap the function to handle different argument counts
      // This resolves the type error while maintaining the correct behavior
      return (deleteCommunityMessage as Function)(messageId, userId);
    }
  };
  
  const LoadingMessageSkeleton = () => (
    <div className="bg-[#151D29]/80 backdrop-blur-lg border border-white/5 rounded-lg p-4 animate-pulse">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#1a2533]" />
          <div className="h-4 bg-[#1a2533] rounded w-24" />
        </div>
        <div className="h-4 bg-[#1a2533] rounded w-16" />
      </div>
      <div className="space-y-2">
        <div className="h-4 bg-[#1a2533] rounded w-3/4" />
        <div className="h-4 bg-[#1a2533] rounded w-1/2" />
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 py-4 px-2 sm:p-8 md:pl-24 flex flex-col w-full overflow-hidden" style={{ height: '100dvh' }}>
      {/* Online members - visible on all breakpoints */}
      <OnlineMembers />
      {/* Header with view toggle - glossy design */}
      <div className="flex justify-between items-center mb-8">
        {viewMode === 'threaded' && selectedThread ? (
          <div className="flex items-center gap-4 relative">
            {/* Back button with glow effect */}
            <motion.button
              onClick={handleBackToFlatView}
              className="flex items-center gap-2 text-white text-sm bg-[#151D29]/60 backdrop-blur-md px-4 py-2 rounded-lg border border-[#c9fffc]/10 shadow-md hover:border-[#c9fffc]/30 hover:shadow-[#c9fffc]/10 transition-all duration-300"
              whileHover={{ scale: 1.05, boxShadow: '0 0 10px 0 rgba(201, 255, 252, 0.2)' }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="relative">
                <ChevronLeft size={18} className="text-[#c9fffc] relative z-10" />
                <div className="absolute inset-0 bg-[#c9fffc]/20 rounded-full blur-sm"></div>
              </div>
              <span>Back to All Discussions</span>
            </motion.button>
            
            {/* Thread view title with neon underline */}
            <div className="relative">
              <h1 className="text-2xl font-bold text-white">Thread View</h1>
              <div className="absolute -bottom-1 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#c9fffc] to-transparent opacity-70"></div>
            </div>
          </div>
        ) : (
          <div className="flex-1 w-full max-w-full">
            {/* Community chat title with neon underline */}
            <div className="relative">
              <h1 className="text-2xl font-bold text-white">Community Chat</h1>
              <div className="absolute -bottom-1 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#c9fffc] to-transparent opacity-70"></div>
            </div>
            
            {/* Commented out view mode toggle but preserved threading functionality */}
            {/* 
            <div className="flex bg-[#151D29]/40 backdrop-blur-md p-1 rounded-lg border border-[#c9fffc]/10 shadow-inner">
              <motion.button 
                onClick={() => setViewMode('flat')}
                className={`px-4 py-1.5 text-sm rounded-lg relative overflow-hidden ${viewMode === 'flat' ? 'text-[#151D29] font-medium' : 'text-[#c9fffc]/70 hover:text-[#c9fffc]'}`}
                whileHover={viewMode !== 'flat' ? { scale: 1.05 } : {}}
                whileTap={viewMode !== 'flat' ? { scale: 0.95 } : {}}
              >
                {viewMode === 'flat' && (
                  <motion.div 
                    className="absolute inset-0 bg-[#c9fffc] rounded-lg -z-10"
                    layoutId="viewModeHighlight"
                    initial={false}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}
                Flat
              </motion.button>
              <motion.button 
                onClick={() => setViewMode('threaded')}
                className={`px-4 py-1.5 text-sm rounded-lg relative overflow-hidden ${viewMode === 'threaded' ? 'text-[#151D29] font-medium' : 'text-[#c9fffc]/70 hover:text-[#c9fffc]'}`}
                whileHover={viewMode !== 'threaded' ? { scale: 1.05 } : {}}
                whileTap={viewMode !== 'threaded' ? { scale: 0.95 } : {}}
              >
                {viewMode === 'threaded' && (
                  <motion.div 
                    className="absolute inset-0 bg-[#c9fffc] rounded-lg -z-10"
                    layoutId="viewModeHighlight"
                    initial={false}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}
                Threaded
              </motion.button>
            </div>
            */}
          </div>
        )}
        
        {viewMode === 'threaded' && !selectedThread && (
          <button
            onClick={() => setShowNewThreadForm(!showNewThreadForm)}
            className="flex items-center gap-1 text-white text-sm bg-blue-600 px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <MessageSquare size={16} />
            New Thread
          </button>
        )}
      </div>
      
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">
          <p className="text-red-400">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 text-sm text-[#c9fffc] hover:text-white transition-colors"
          >
            Click to retry
          </button>
        </div>
      )}
      
      {/* Show new thread form if active */}
      {showNewThreadForm && (
        <div className="mb-4">
          <ThreadReplyForm
            onSubmit={handleCreateThread}
            onCancel={() => setShowNewThreadForm(false)}
            placeholder="Start a new discussion..."
          />
        </div>
      )}
      
      <div ref={containerRef} className="overflow-y-auto overflow-x-hidden hide-scrollbar flex-1 flex-grow pb-24 max-w-full w-full relative z-20" style={{ height: 'calc(100dvh - 10rem)' }}>
        {viewMode === 'flat' ? (
          // Flat view (original implementation)
          <>
            <CategoryFilter
              categories={categories}
              selectedCategory={selectedCategory}
              onSelectCategory={setSelectedCategory}
              isAdmin={isAdmin}
              rightAdornment={
                <OnlineMembers variant="inline" />
              }
            />

            

            <PinnedMessages
              messages={pinnedMessages}
              isAdmin={isAdmin}
              currentUserId={currentUser?.id}
              onTogglePin={togglePin}
              onDelete={handleDeleteMessage}
              onToggleLike={handleToggleLike}
              onViewThread={handleSelectThread}
            />
            
            <AnimatePresence mode="wait">
              {initialLoading ? (
                <motion.div 
                  className="space-y-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  key="loading-skeletons"
                >
                  {[...Array(5)].map((_, i) => (
                    <LoadingMessageSkeleton key={i} />
                  ))}
                </motion.div>
              ) : isCategoryLoading ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  key="category-loading"
                >
                  <CategoryLoadingIndicator isLoading={true} />
                  <div className="space-y-4 opacity-40 pointer-events-none">
                    <MessageList
                      messages={messages}
                      isAdmin={isAdmin}
                      polls={polls}
                      currentUserId={currentUser?.id}
                      onTogglePin={togglePin}
                      onDelete={handleDeleteMessage}
                      onToggleLike={handleToggleLike}
                      onAddComment={addComment}
                      onVotePoll={votePoll}
                      onDeletePoll={deletePoll}
                      onViewThread={handleSelectThread}
                      onOpenCategoryModal={handleOpenCategoryModal}
                    />
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  key="message-list"
                >
                  <MessageList
                      messages={messages}
                      isAdmin={isAdmin}
                      polls={polls}
                      currentUserId={currentUser?.id}
                      onTogglePin={togglePin}
                      onDelete={handleDeleteMessage}
                      onToggleLike={handleToggleLike}
                      onAddComment={addComment}
                      onVotePoll={votePoll}
                      onDeletePoll={deletePoll}
                      onViewThread={handleSelectThread}
                      onOpenCategoryModal={handleOpenCategoryModal}
                    />
                    {/* Bottom loader / end-of-list indicator */}
                    <div className="py-4 flex justify-center">
                      {isLoadingMore && (
                        <span className="text-sm text-gray-400">Loading more…</span>
                      )}
                      {!isLoadingMore && !hasMore && messages.length > 0 && (
                        <span className="text-xs text-gray-500">You’re up to date.</span>
                      )}
                    </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        ) : (
          // Threaded view (new implementation)
          <AnimatePresence mode="wait">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {loading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="animate-pulse space-y-2">
                      <div className="h-24 bg-[#151D29]/60 rounded-lg mb-2"></div>
                      <div className="h-20 bg-[#151D29]/60 rounded-lg ml-6 mb-2"></div>
                      <div className="h-16 bg-[#151D29]/60 rounded-lg ml-12 mb-2"></div>
                    </div>
                  ))}
                </div>
              ) : (
                <ThreadTree
                  messages={threadedMessages}
                  currentUserId={currentUser?.id}
                  isAdmin={isAdmin}
                  onReply={handleAddReply}
                  onToggleLike={handleToggleLike}
                  onDelete={handleDeleteMessage}
                  onEdit={editMessage}
                  onAddComment={addComment}
                />
              )}
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      {/* Only show message form in flat view or when no thread is selected */}
      {(viewMode === 'flat' || !selectedThread) && (
        <div className="fixed bottom-0 right-0 sm:right-auto z-20 w-full md:w-[calc(100%-12rem)] md:left-1/2 md:-translate-x-1/2 md:transform px-2 sm:px-8 max-w-[1800px] bg-[#0F1520] sm:bg-transparent">
          <NewMessageForm 
            onSubmit={(content, category) => {
              // Always use the currently selected category if a specific one isn't provided
              // This ensures messages go to the right category when user has selected one
              const categoryToUse = category || selectedCategory || '';
              console.log('Submitting message with category:', categoryToUse);
              return addMessage(content, categoryToUse);
            }}
            onCreatePoll={createPoll}
            isAdmin={isAdmin}
            selectedCategory={selectedCategory}
          />
        </div>
      )}

      {/* Category Selection Modal */}
      {showCategoryModal && selectedMessageForCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-gray-900 rounded-lg p-6 w-full max-w-md mx-4 border border-gray-700">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white">Move to Category</h3>
              <button
                onClick={() => {
                  setShowCategoryModal(false);
                  setSelectedMessageForCategory(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            {selectedMessageForCategory.currentCategory && (
              <div className="mb-4 p-3 bg-gray-800 rounded-lg">
                <span className="text-sm text-gray-400">Current category:</span>
                <span className="text-[#c9fffc] font-medium ml-2">
                  {selectedMessageForCategory.currentCategory.name}
                </span>
              </div>
            )}
            
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => handleChangeCategory(category.id)}
                  className={`w-full px-4 py-3 text-left rounded-lg transition-colors ${
                    selectedMessageForCategory.currentCategory?.id === category.id
                      ? 'bg-[#c9fffc]/20 text-[#c9fffc] border border-[#c9fffc]/30'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-transparent'
                  }`}
                >
                  {category.name}
                  {selectedMessageForCategory.currentCategory?.id === category.id && (
                    <span className="ml-2 text-xs text-[#c9fffc]/70">(current)</span>
                  )}
                </button>
              ))}
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowCategoryModal(false);
                  setSelectedMessageForCategory(null);
                }}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Thread Modal */}
      <MessageThreadModal
        isOpen={isThreadModalOpen}
        threadId={selectedThread}
        onClose={handleCloseThreadModal}
        currentUserId={currentUser?.id}
        isAdmin={isAdmin}
        onAddComment={addComment}
      />
    </div>
  );
}